import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  QueryConstraint,
  Timestamp,
  startAfter,
  endBefore,
  limitToLast,
  limit,
  DocumentSnapshot,
} from "firebase/firestore";
import dayjs from "dayjs";
import { db } from "./config";
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { PaginatedQueryOptions } from "@/types/filters.types";

// Pagination result type
export interface PaginationResult<T> {
  data: T[];
  lastVisible: DocumentSnapshot | null;
  firstVisible: DocumentSnapshot | null;
  hasNextPage: boolean;
}

// Extend dayjs with required plugins
dayjs.extend(utc)
dayjs.extend(timezone)

const COLOMBIAN_TIMEZONE = 'America/Bogota'

// Helper to get current timestamp in Colombian timezone
export const getColombianTimestamp = (): Date => {
  return dayjs().tz(COLOMBIAN_TIMEZONE).toDate()
}


// Helper function to convert Firestore Timestamps to strings and ensure serializable data
const convertTimestamps = (data: any, visited = new WeakSet()): any => {
  if (!data) return data;

  // Handle primitive types
  if (typeof data !== "object") return data;

  // Handle Date objects
  if (data instanceof Date) {
    return data.toISOString();
  }

  // Handle Timestamp conversion
  if (data instanceof Timestamp) {
    return data.toDate().toISOString();
  }

  // Prevent circular references
  if (visited.has(data)) return undefined;
  visited.add(data);

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => convertTimestamps(item, visited));
  }

  // Handle objects - create a plain object copy
  const converted: Record<string, any> = {};

  // Only process own enumerable properties to avoid prototype chain issues
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const value = data[key];

      // Skip functions and undefined values
      if (typeof value === "function" || value === undefined) {
        continue;
      }

      if (value instanceof Timestamp) {
        converted[key] = value.toDate().toISOString();
      } else if (value instanceof Date) {
        converted[key] = value.toISOString();
      } else if (typeof value === "object" && value !== null) {
        const convertedValue = convertTimestamps(value, visited);
        // Only add if not circular/undefined
        if (convertedValue !== undefined) {
          converted[key] = convertedValue;
        }
      } else {
        // Primitives (string, number, boolean, null)
        converted[key] = value;
      }
    }
  }

  return converted;
};

export const firestoreService = {
  // Get a single document
  getDocument: async (collectionName: string, docId: string) => {
    const docRef = doc(db, collectionName, docId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = convertTimestamps(docSnap.data());
      return { id: docSnap.id, ...data };
    }
    return null;
  },

  // Get all documents from a collection
  getDocuments: async (collectionName: string, constraints: QueryConstraint[] = []) => {
    return await fetchDocumentsByConstraints(collectionName, constraints)
  },

  // Add a new document
  addDocument: async (collectionName: string, data: any) => {
    const collectionRef = collection(db, collectionName);
    const docRef = await addDoc(collectionRef, {
      ...data,
      createdAt: getColombianTimestamp(),
    })
    return docRef.id
  },

  // Update a document
  updateDocument: async (collectionName: string, docId: string, data: any) => {
    const docRef = doc(db, collectionName, docId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: getColombianTimestamp(),
    })
  },

  // Delete a document
  deleteDocument: async (collectionName: string, docId: string) => {
    const docRef = doc(db, collectionName, docId);
    await deleteDoc(docRef);
  },

  // Query helpers
  where,
  orderBy,
  // Get documents with server-side constraints + client-side fallback filtering
  getDocumentsWithFilters: async (collectionName: string, filters: { name?: string; tag?: string; date?: string; editor?: string } = {}) => {
    const constraints = buildQueryConstraints(filters)

    // Reuse primary fetch helper to retrieve docs with server-side constraints
    const docs = await fetchDocumentsByConstraints(collectionName, constraints)

    // Client-side filtering for tag, name, editor and date (case-insensitive / date normalization)
    const filtered = docs.filter((d: any) => {
      // Tag - exact match (case-insensitive)
      if (filters.tag && (d.tag || '').toLowerCase() !== (filters.tag || '').toLowerCase()) {
        return false
      }

      // Name
      if (
        filters.name &&
        !(d.name || "")
          .toLowerCase()
          .includes((filters.name || "").toLowerCase())
      ) {
        return false;
      }

      // Editor
      if (
        filters.editor &&
        !(d.lastEditor || "")
          .toLowerCase()
          .includes((filters.editor || "").toLowerCase())
      ) {
        return false;
      }

      // Date: compare YYYY-MM-DD
      if (filters.date) {
        try {
          const filterDate = dayjs(filters.date).format("YYYY-MM-DD");
          // Parse lastEdition with the correct format: DD/MM/YYYY HH:mm
          const docDate = dayjs(d.lastEdition, "DD/MM/YYYY HH:mm").format("YYYY-MM-DD");
          if (filterDate !== docDate) return false;
        } catch (e) {
          // if parsing fails, ignore date filter for that doc
        }
      }

      return true;
    });

    return filtered;
  },
  getDocumentsWithPagination: async <T = any>(
    collectionName: string,
    constraints: Array<{
      field: string;
      operator:
        | "=="
        | "!="
        | "<"
        | "<="
        | ">"
        | ">="
        | "array-contains"
        | "in"
        | "array-contains-any";
      value: any;
    }> = [],
    paginationOptions?: PaginatedQueryOptions
  ): Promise<PaginationResult<T> | T[]> => {
    try {
      const collectionRef = collection(db, collectionName);
      const queryConstraints: QueryConstraint[] = [];
  
      // Aplicar filtros
      if (constraints.length > 0) {
        constraints.forEach((constraint) => {
          queryConstraints.push(
            where(constraint.field, constraint.operator, constraint.value)
          );
        });
      }
  
      queryConstraints.push(orderBy('createdAt', 'desc'));
  
      if (paginationOptions) {
        const { pageSize, direction, lastVisible, firstVisible } = paginationOptions;
  
        if (pageSize) {
          const actualLimit = pageSize + 1;
  
          if (direction === 'next' && lastVisible) {
            queryConstraints.push(startAfter(lastVisible));
            queryConstraints.push(limit(actualLimit));
          }
          else if (direction === 'previous' && firstVisible) {
            queryConstraints.push(endBefore(firstVisible));
            queryConstraints.push(limitToLast(actualLimit));
          }
          else {
            queryConstraints.push(limit(actualLimit));
          }
        }
      }
  
      const q = query(collectionRef, ...queryConstraints);
      const snapshot = await getDocs(q);

      if (paginationOptions?.pageSize) {
        const allDocs = snapshot.docs;
        
        const hasMore = allDocs.length > paginationOptions.pageSize;
        
        const actualDocs = hasMore 
          ? allDocs.slice(0, paginationOptions.pageSize) 
          : allDocs;
  
        const docs = actualDocs.map((doc) => ({
          id: doc.id,
          ...convertTimestamps(doc.data())
        })) as T[];

        const lastVisible = actualDocs[actualDocs.length - 1] || null;
        const firstVisible = actualDocs[0] || null;

        return {
          data: docs,
          lastVisible,
          firstVisible,
          hasNextPage: hasMore,
        } as PaginationResult<T>;
      }

      // Sin paginación, retornar todos
      const docs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...convertTimestamps(doc.data())
      })) as T[];

      return docs;
    } catch (error) {
      console.error("Error getting documents with query:", error);
      throw error;
    }
  }
}

async function fetchDocumentsByConstraints(collectionName: string, constraints: QueryConstraint[] = []) {
  const collectionRef = collection(db, collectionName)
  const q = constraints.length > 0 ? query(collectionRef, ...constraints) : collectionRef
  const querySnapshot = await getDocs(q)

  return querySnapshot.docs.map(docSnap => {
    const data = convertTimestamps(docSnap.data())
    return {
      id: docSnap.id,
      ...data,
    }
  })
}

export function buildQueryConstraints(_filters: { name?: string; tag?: string; date?: string; editor?: string } = {}): QueryConstraint[] {
  const constraints: QueryConstraint[] = []

  try {
    constraints.push(orderBy('lastEdition', 'desc'))
  } catch (err) {
  }

  return constraints
}
